import { useEffect, useState } from "react";
import type { DirectoryUser } from "@/types/calendar";

let inflightPromise: Promise<DirectoryUser[]> | null = null;
let cached: { users: DirectoryUser[]; expiresAt: number } | null = null;

async function fetchUsers(): Promise<DirectoryUser[]> {
  if (cached && cached.expiresAt > Date.now()) return cached.users;
  if (inflightPromise) return inflightPromise;
  inflightPromise = (async () => {
    const res = await fetch("/api/users");
    if (!res.ok) throw new Error(`users_fetch_failed status=${res.status}`);
    const data = (await res.json()) as { users: DirectoryUser[] };
    cached = { users: data.users, expiresAt: Date.now() + 10 * 60 * 1000 };
    return data.users;
  })().finally(() => {
    inflightPromise = null;
  });
  return inflightPromise;
}

export function useUsers() {
  const [users, setUsers] = useState<DirectoryUser[]>(cached?.users || []);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchUsers()
      .then((list) => {
        if (!cancelled) {
          setUsers(list);
          setError(null);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { users, loading, error };
}
